import { AccessFunctionFormData, AccessFunctionInterfaceFormData } from 'data/accessFunction/accessFunction.types';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateSection
} from 'data/template/template.types';
import cloneDeep from 'lodash.clonedeep';
import _ from 'lodash';
import { MultiselectTypeahead } from 'shared/form/custom/multiselectTypeahead';
import { NodeData } from 'data/nodes/nodes.types';
import { Network } from 'data/network/network.types';
import { setOptionForTemplateSection, getNetworkOptionsMap } from './accessFunctionTemplate.adapter';

enum AFTypes {
  AlcatelLucentMME = 'ALU_MME',
  AlcatelLucentSGW = 'ALU_SGW',
  EricssonSAEGWPGW = 'ERK_PGW',
  EricssonSAEGWSGW = 'ERK_SGW',
  EricssonvMME = 'ERK_VMME',
  GizmoETSI = 'ETSI_GIZMO',
  KodiakMCPTT = 'KODIAK_MCPTT',
  NokiaSAEGWPGW = 'NSN_SAEGW_PGW',
  NokiaSAEGWSGW = 'NSN_SAEGW',
  AcmePacketSBC = 'GENVOIP',
  CiscoStarentGGSN = 'STA_GGSN',
  AlcatelLucentWebGateway = 'LU3G_CSCF',
  EricssonSCEF = 'ERK_SCEF',
  HPHlr = 'HP_HLR',
  NokiaSiemensCSCF = 'NSN_CSCF',
  NokiaHSSSDM = 'NSN_SDM',
  SonusSBC = 'SONUS_SBC',
  MavenirMStore = 'MVNR_RCS_MSTORE',
  MavenirSMFIWF = 'MV_SMSIWF',
  MavenirRCSAS = 'MVNR_RCS_AS',
  HPAAA = 'HP_AAA',
  NokiaSiemensHlrHssPgrw = 'NSNHLR_SDM_PRGW',
  ALUHLR = 'ALU_HLR',
  OracleCamiantPcrf = 'PCRF',
  McAfeeVPN = 'MCFVPN'
}

export default function adaptTemplate4GFieldsForGRCreate(template: FormTemplate | undefined, isGrEnabled: boolean) {
  if (_.isNil(template)) {
    return;
  }

  if (!isGrEnabled) {
    return template;
  }

  template.formId = Date.now().toString();

  switch (template.key) {
    case AFTypes.AlcatelLucentMME: {
      return adaptTemplateForAluMME(template);
    }
    case AFTypes.AlcatelLucentSGW: {
      return adaptTemplateForAluSGW(template);
    }
    case AFTypes.EricssonSAEGWPGW: {
      return adaptTemplateForErkPGW(template);
    }
    case AFTypes.EricssonSAEGWSGW: {
      return adaptTemplateForErkSGW(template);
    }
    case AFTypes.EricssonvMME: {
      return adaptTemplateForErkvMME(template);
    }
    case AFTypes.GizmoETSI: {
      return adaptTemplateForEtsiGizmo(template);
    }
    case AFTypes.KodiakMCPTT: {
      return adaptTemplateForKodiakMcptt(template);
    }
    case AFTypes.NokiaSAEGWPGW: {
      return adaptTemplateForNsnSaegwPgw(template);
    }
    case AFTypes.NokiaSAEGWSGW: {
      return adaptTemplateForNsnSaegw(template);
    }
    case AFTypes.AcmePacketSBC: {
      return adaptTemplateForAcmePacketSBC(template);
    }
    case AFTypes.CiscoStarentGGSN: {
      return adaptTemplateForCiscoStarentGGSN(template);
    }
    case AFTypes.AlcatelLucentWebGateway: {
      return adaptTemplateForAlcatelLucentWebGateway(template);
    }
    case AFTypes.EricssonSCEF: {
      return adaptTemplateForEricssonSCEF(template);
    }
    case AFTypes.HPHlr: {
      return adaptTemplateForHPHlr(template);
    }
    case AFTypes.NokiaSiemensCSCF: {
      return adaptTemplateForNokiaSiemensCSCF(template);
    }
    case AFTypes.NokiaHSSSDM: {
      return adaptTemplateForNokiaHSSSDM(template);
    }
    case AFTypes.SonusSBC: {
      return adaptTemplateForSonusSBC(template);
    }
    case AFTypes.MavenirMStore: {
      return adaptTemplateForMavenirMStore(template);
    }
    case AFTypes.MavenirSMFIWF: {
      return adaptTemplateForMavenirSMFIWF(template);
    }
    case AFTypes.MavenirRCSAS: {
      return adaptTemplateForMavenirRCSAS(template);
    }
    case AFTypes.HPAAA: {
      return adaptTemplateForHPAAA(template);
    }
    case AFTypes.NokiaSiemensHlrHssPgrw: {
      return adaptTemplateForNokiaSiemensHlrHssPgrw(template);
    }
    case AFTypes.ALUHLR: {
      return adaptTemplateForALUHLR(template);
    }
    case AFTypes.OracleCamiantPcrf: {
      return adaptTemplateForOracleCamiantPcrf(template);
    }
    case AFTypes.McAfeeVPN: {
      return adaptTemplateForMcAfeeVPN(template);
    }
    default: {
      return template;
    }
  }
}

function adaptTemplateForAluMME(template: FormTemplate) {
  const original = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelect = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (original && multiSelect) {
    original.hide = 'true';
    multiSelect.hide = 'false';
  }
  return template;
}

function adaptTemplateForAluSGW(template: FormTemplate) {
  const originalMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  const originalMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForErkPGW(template: FormTemplate) {
  const originalMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForErkSGW(template: FormTemplate) {
  const originalMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  const { dataDeliveryVia } = (template.contentInterfaces as FormTemplateSection).fields;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    if (dataDeliveryVia?.initial !== 'directFromAF') {
      multiSelectMdf2.hide = 'false';
    }
  }
  const originalMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  const { contentDeliveryVia } = (template.contentInterfaces as FormTemplateSection).fields;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    if (contentDeliveryVia?.initial !== 'directFromAF') {
      multiSelectMdf3.hide = 'false';
    }
  }
  return template;
}

function adaptTemplateForErkvMME(template: FormTemplate) {
  const originalMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  return template;
}

function adaptTemplateForEtsiGizmo(template: FormTemplate) {
  const originalMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  const originalMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForKodiakMcptt(template: FormTemplate) {
  const originalMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  const originalMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForNsnSaegwPgw(template: FormTemplate) {
  const originalMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForNsnSaegw(template: FormTemplate) {
  const originalMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  const originalMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForAcmePacketSBC(template: FormTemplate) {
  const formTemplateSection = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }
  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }

  const originalMdf3 = formTemplateSection.fields.mdf3NodeName;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForCiscoStarentGGSN(template: FormTemplate) {
  const formTemplateSection = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  const originalMdf3 = formTemplateSection.fields.mdf3NodeName;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForAlcatelLucentWebGateway(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces_directFromAF as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (multiSelectMdf3) {
    multiSelectMdf3.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }

  return template;
}

function adaptTemplateForEricssonSCEF(template: FormTemplate) {
  const formTemplateSection = template.dataInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForHPHlr(template: FormTemplate) {
  const formTemplateSection = template.dataInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForNokiaSiemensCSCF(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  const originalMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForNokiaHSSSDM(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  const original = formTemplateSection.fields.mdf2NodeName;
  if (multiSelectMdf2) {
    original.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  return template;
}

function adaptTemplateForSonusSBC(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces_directFromAF as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  const originalMdf3 = (template.provisioningInterfaces_directFromAF as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForMavenirMStore(template: FormTemplate) {
  const formTemplateSection = template.dataInterfaces_directFromAF as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }

  return template;
}

function adaptTemplateForMavenirSMFIWF(template: FormTemplate) {
  const formTemplateSection = template.dataInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }
  return template;
}

function adaptTemplateForMavenirRCSAS(template: FormTemplate) {
  const formTemplateSection = template.contentInterfaces_directFromAF as FormTemplateSection;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (multiSelectMdf3) {
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

function adaptTemplateForHPAAA(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }

  return template;
}

function adaptTemplateForNokiaSiemensHlrHssPgrw(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForALUHLR(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 4);
  }
  return template;
}

function adaptTemplateForOracleCamiantPcrf(template: FormTemplate) {
  const formTemplateSection = template.provisioningInterfaces as FormTemplateSection;
  const multiSelectMdf2 = formTemplateSection.fields.mdf2NodeNameMultiSelect;
  if (multiSelectMdf2) {
    multiSelectMdf2.hide = 'false';
  }

  const originalMdf3 = formTemplateSection.fields.mdf3NodeName;
  const multiSelectMdf3 = formTemplateSection.fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  if (formTemplateSection.metadata.layout && _.isArray(formTemplateSection.metadata.layout.rows)) {
    formTemplateSection.metadata.layout.rows[2] = formTemplateSection.metadata.layout.rows[2].slice(0, 5);
  }
  return template;
}

function adaptTemplateForMcAfeeVPN(template: FormTemplate) {
  const originalMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeName;
  const multiSelectMdf2 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
  if (originalMdf2 && multiSelectMdf2) {
    originalMdf2.hide = 'true';
    multiSelectMdf2.hide = 'false';
  }
  const originalMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName;
  const multiSelectMdf3 = (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
  if (originalMdf3 && multiSelectMdf3) {
    originalMdf3.hide = 'true';
    multiSelectMdf3.hide = 'false';
  }
  return template;
}

export function adapt4GAfsForGRToSubmit(formData: AccessFunctionFormData, data: Partial<AccessFunctionFormData>): AccessFunctionFormData {
  switch (formData.tag) {
    case AFTypes.AlcatelLucentMME: {
      if (_.isArray(data.mdf2NodeNameMultiSelect)) {
        formData.mdf2NodeName = getSpaceSeparateValues(data.mdf2NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.AlcatelLucentSGW: {
      if (_.isArray(data.mdf2NodeNameMultiSelect)) {
        formData.mdf2NodeName = getSpaceSeparateValues(data.mdf2NodeNameMultiSelect);
      }
      if (_.isArray(data.mdf3NodeNameMultiSelect)) {
        formData.mdf3NodeName = getSpaceSeparateValues(data.mdf3NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.EricssonSAEGWPGW: {
      if (_.isArray(data.mdf3NodeNameMultiSelect)) {
        formData.mdf3NodeName = getSpaceSeparateValues(data.mdf3NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.EricssonSAEGWSGW: {
      if (_.isArray(data.mdf2NodeNameMultiSelect)) {
        formData.mdf2NodeName = getSpaceSeparateValues(data.mdf2NodeNameMultiSelect);
      }
      if (_.isArray(data.mdf3NodeNameMultiSelect)) {
        formData.mdf3NodeName = getSpaceSeparateValues(data.mdf3NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.EricssonvMME: {
      if (_.isArray(data.mdf2NodeNameMultiSelect)) {
        formData.mdf2NodeName = getSpaceSeparateValues(data.mdf2NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.GizmoETSI: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeName = formData.mdf2NodeName;
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.KodiakMCPTT: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeName = formData.mdf2NodeName;
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.NokiaSAEGWPGW: {
      if (_.isArray(data.mdf3NodeNameMultiSelect)) {
        formData.mdf3NodeName = getSpaceSeparateValues(data.mdf3NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.NokiaSAEGWSGW: {
      if (_.isArray(data.mdf2NodeNameMultiSelect)) {
        formData.mdf2NodeName = getSpaceSeparateValues(data.mdf2NodeNameMultiSelect);
      }
      if (_.isArray(data.mdf3NodeNameMultiSelect)) {
        formData.mdf3NodeName = getSpaceSeparateValues(data.mdf3NodeNameMultiSelect);
      }
      break;
    }
    case AFTypes.AcmePacketSBC: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.CiscoStarentGGSN: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.AlcatelLucentWebGateway: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.EricssonSCEF: {
      if (formData.dataInterfaces && formData.dataInterfaces.length > 0) {
        const firstItem = formData.dataInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.HPHlr: {
      if (formData.dataInterfaces && formData.dataInterfaces.length > 0) {
        const firstItem = formData.dataInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.NokiaSiemensCSCF: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.NokiaHSSSDM: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.SonusSBC: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.MavenirMStore: {
      if (formData.dataInterfaces && formData.dataInterfaces.length > 0) {
        const firstItem = formData.dataInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.MavenirSMFIWF: {
      if (formData.dataInterfaces && formData.dataInterfaces.length > 0) {
        const firstItem = formData.dataInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.MavenirRCSAS: {
      if (formData.contentInterfaces && formData.contentInterfaces.length > 0) {
        const firstItem = formData.contentInterfaces[0];
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.HPAAA: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.NokiaSiemensHlrHssPgrw: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        formData.provisioningInterfaces.forEach((pi) => {
          if (_.isArray(pi.mdf2NodeNameMultiSelect)) {
            const spaceSeparatedMdf2NodeNames = getSpaceSeparateValues(pi.mdf2NodeNameMultiSelect);
            formData.mdf2NodeName = spaceSeparatedMdf2NodeNames;
            pi.mdf2NodeName = spaceSeparatedMdf2NodeNames;
            pi.mdf2NodeNameMultiSelect = undefined;
          }
        });
      }
      break;
    }
    case AFTypes.ALUHLR: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    case AFTypes.OracleCamiantPcrf: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        formData.provisioningInterfaces.forEach((pi) => {
          if (_.isArray(pi.mdf2NodeNameMultiSelect)) {
            const spaceSeparatedMdf2NodeNames = getSpaceSeparateValues(pi.mdf2NodeNameMultiSelect);
            formData.mdf2NodeName = spaceSeparatedMdf2NodeNames;
            pi.mdf2NodeName = spaceSeparatedMdf2NodeNames;
            pi.mdf2NodeNameMultiSelect = undefined;
          }
          if (_.isArray(pi.mdf3NodeNameMultiSelect)) {
            const spaceSeparatedMdf3NodeNames = getSpaceSeparateValues(pi.mdf3NodeNameMultiSelect);
            formData.mdf3NodeName = spaceSeparatedMdf3NodeNames;
            pi.mdf3NodeName = spaceSeparatedMdf3NodeNames;
            pi.mdf3NodeNameMultiSelect = undefined;
          }
        });
      }
      break;
    }
    case AFTypes.McAfeeVPN: {
      if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
        const firstItem = formData.provisioningInterfaces[0];
        if (_.isArray(firstItem.mdf2NodeNameMultiSelect)) {
          formData.mdf2NodeName = getSpaceSeparateValues(firstItem.mdf2NodeNameMultiSelect);
          firstItem.mdf2NodeName = formData.mdf2NodeName;
          firstItem.mdf2NodeNameMultiSelect = undefined;
        }
        if (_.isArray(firstItem.mdf3NodeNameMultiSelect)) {
          formData.mdf3NodeName = getSpaceSeparateValues(firstItem.mdf3NodeNameMultiSelect);
          firstItem.mdf3NodeNameMultiSelect = undefined;
        }
      }
      break;
    }
    default: {
      return formData;
    }
  }
  return formData;
}

function getSpaceSeparateValues(arr: MultiselectTypeahead[]) {
  let ret = '';
  ret = arr.map(((x) => x.value)).reduce((acc: string, curr: string) => {
    acc += ` ${curr}`;
    return acc;
  }, '' as string).substring(1);
  return ret;
}

export function adaptTemplate4GFieldsForGRExisting(
  template: FormTemplate | undefined,
  formData: AccessFunctionFormData,
  isGrEnabled: boolean,
  xcipioNodes?: NodeData[],
  networks?: Network[],
  originalTemplates?: { [key: string]: FormTemplate; } | undefined
): FormTemplate | undefined {
  if (_.isNil(template)) {
    return;
  }

  if (!isGrEnabled) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  adaptTemplate4GFieldsForGRCreate(clonedTemplate, isGrEnabled);

  switch (template.key) {
    case AFTypes.AlcatelLucentMME: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        formData.mdf2NodeNameMultiSelect = mdf2s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.AlcatelLucentSGW: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        formData.mdf2NodeNameMultiSelect = mdf2s.map(mapStringToOption);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        formData.mdf3NodeNameMultiSelect = mdf3s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.EricssonSAEGWPGW: {
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        formData.mdf3NodeNameMultiSelect = mdf3s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.EricssonSAEGWSGW: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        formData.mdf2NodeNameMultiSelect = mdf2s.map(mapStringToOption);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        formData.mdf3NodeNameMultiSelect = mdf3s.map(mapStringToOption);
      }

      const originalMdf2 = (clonedTemplate.dataInterfaces as FormTemplateSection).fields.mdf2NodeName;
      const multiSelectMdf2 = (clonedTemplate.dataInterfaces as FormTemplateSection).fields.mdf2NodeNameMultiSelect;
      if (originalMdf2 && multiSelectMdf2) {
        originalMdf2.hide = 'true';
        if (formData.dataDeliveryVia !== 'directFromAF') {
          multiSelectMdf2.hide = 'false';
        } else {
          multiSelectMdf2.hide = 'true';
        }
      }

      const originalMdf3 = (clonedTemplate.contentInterfaces as FormTemplateSection).fields.mdf3NodeName;
      const multiSelectMdf3 = (clonedTemplate.contentInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect;
      if (originalMdf3 && multiSelectMdf3) {
        originalMdf3.hide = 'true';
        if (formData.contentDeliveryVia !== 'directFromAF') {
          multiSelectMdf3.hide = 'false';
        } else {
          multiSelectMdf3.hide = 'true';
        }
      }
      break;
    }
    case AFTypes.EricssonvMME: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        formData.mdf2NodeNameMultiSelect = mdf2s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.GizmoETSI: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
      }
      break;
    }
    case AFTypes.KodiakMCPTT: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
      }
      break;
    }
    case AFTypes.NokiaSAEGWPGW: {
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        formData.mdf3NodeNameMultiSelect = mdf3s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.NokiaSAEGWSGW: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        formData.mdf2NodeNameMultiSelect = mdf2s.map(mapStringToOption);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        formData.mdf3NodeNameMultiSelect = mdf3s.map(mapStringToOption);
      }
      break;
    }
    case AFTypes.AcmePacketSBC: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }

      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        adaptCcdestipField(formTemplateSection.fields.ccdestip, xcipioNodes ?? [], mdf3s[0]);
      }
      break;
    }
    case AFTypes.CiscoStarentGGSN: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }

      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        adaptCcdestipField(formTemplateSection.fields.ccdestip, xcipioNodes ?? [], mdf3s[0]);
      }
      break;
    }
    case AFTypes.AlcatelLucentWebGateway: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }

      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
      }
      break;
    }
    case AFTypes.EricssonSCEF: {
      const formTemplateSection = (clonedTemplate.dataInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.dataInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.ownnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
      }
      break;
    }
    case AFTypes.HPHlr: {
      const formTemplateSection = (clonedTemplate.dataInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.dataInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.ownnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
      }
      break;
    }
    case AFTypes.NokiaSiemensCSCF: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }

      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        adaptCcdestipField(formTemplateSection.fields.ccdestip, xcipioNodes ?? [], mdf3s[0]);
      }
      break;
    }
    case AFTypes.NokiaHSSSDM: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }
      break;
    }
    case AFTypes.SonusSBC: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }

      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        adaptCcdestipField(formTemplateSection.fields.ccdestip, xcipioNodes ?? [], mdf3s[0]);
      }
      break;
    }
    case AFTypes.MavenirMStore: {
      const formTemplateSection = (clonedTemplate.dataInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.dataInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.ownnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
      }
      break;
    }
    case AFTypes.MavenirSMFIWF: {
      const formTemplateSection = (clonedTemplate.dataInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.dataInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.ownnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
      }
      break;
    }
    case AFTypes.MavenirRCSAS: {
      const formTemplateSection = (clonedTemplate.contentInterfaces_directFromAF as FormTemplateSection);
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.contentInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf3s[0]);
        formTemplateSection.fields.ownnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
      }
      break;
    }
    case AFTypes.HPAAA: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }
      break;
    }
    case AFTypes.NokiaSiemensHlrHssPgrw: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }
      break;
    }
    case AFTypes.ALUHLR: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }
      break;
    }
    case AFTypes.OracleCamiantPcrf: {
      const formTemplateSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
        const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === mdf2s[0]);
        formTemplateSection.fields.cdnetid.options = [];
        setOptionForTemplateSection(formTemplateSection, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
        adaptCcdestipField(formTemplateSection.fields.ccdestip, xcipioNodes ?? [], mdf3s[0]);
      }
      break;
    }
    case AFTypes.McAfeeVPN: {
      if (formData.mdf2NodeName) {
        const mdf2s = formData.mdf2NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf2NodeNameMultiSelect', mdf2s);
      }
      if (formData.mdf3NodeName) {
        const mdf3s = formData.mdf3NodeName?.split(' ') || [];
        copyNodeNameToMultiSelect(formData.provisioningInterfaces, 'mdf3NodeNameMultiSelect', mdf3s);
      }
      break;
    }
    default: {
      return clonedTemplate;
    }
  }

  return clonedTemplate;
}

function mapStringToOption(o: string) {
  return { label: o, value: o };
}

function copyNodeNameToMultiSelect(interfaces: AccessFunctionInterfaceFormData[] | undefined, multiSelectField: 'mdf2NodeNameMultiSelect' | 'mdf3NodeNameMultiSelect', nodeNames: string[]) {
  if (interfaces && interfaces.length > 0) {
    interfaces?.map((i) => {
      (i as AccessFunctionInterfaceFormData)[multiSelectField] = nodeNames.map(mapStringToOption);
    });
  }
}

export function adaptTemplateFor4GAfsForGROnFieldUpdate(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[],
  networks?: Network[],
  originalTemplates?: { [key: string]: FormTemplate; } | undefined
): void {
  if (template == null || !originalTemplates || fieldValue === undefined) {
    return;
  }
  template.formId = Date.now().toString();
  const original = originalTemplates[template.key] ?? undefined;

  switch (template.key) {
    case AFTypes.AcmePacketSBC: {
      const formTemplate = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);

          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          adaptCcdestipField(formTemplate.fields.ccdestip, xcipioNodes ?? [], fieldValue[0].value);
        } else {
          formTemplate.fields.ccdestip.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.CiscoStarentGGSN: {
      const formTemplate = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);

          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          adaptCcdestipField(formTemplate.fields.ccdestip, xcipioNodes ?? [], fieldValue[0].value);
        } else {
          formTemplate.fields.ccdestip.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.AlcatelLucentWebGateway: {
      const formTemplate = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ccnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ccnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].ccnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ccnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.EricssonSCEF: {
      const formTemplate = (template.dataInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.dataInterfaces as FormTemplateSection).fields.ownnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ownnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
          if (data.dataInterfaces && data.dataInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.dataInterfaces[0].ownnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ownnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.HPHlr: {
      const formTemplate = (template.dataInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.dataInterfaces as FormTemplateSection).fields.ownnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ownnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
          if (data.dataInterfaces && data.dataInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.dataInterfaces[0].ownnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ownnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.NokiaSiemensCSCF: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.ccdestip.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          adaptCcdestipField(formTemplate.fields.ccdestip, xcipioNodes ?? [], fieldValue[0].value);
        } else {
          formTemplate.fields.ccdestip.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.NokiaHSSSDM: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.SonusSBC: {
      const formTemplate = (template.provisioningInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          adaptCcdestipField(formTemplate.fields.ccdestip, xcipioNodes ?? [], fieldValue[0].value);
        } else {
          const originalOptions = cloneDeep((original.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.options) ?? [];
          formTemplate.fields.ccdestip.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.MavenirMStore: {
      const formTemplate = (template.dataInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.dataInterfaces_directFromAF as FormTemplateSection).fields.ownnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ownnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
          if (data.dataInterfaces && data.dataInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.dataInterfaces[0].ownnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ownnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.MavenirSMFIWF: {
      const formTemplate = (template.dataInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.dataInterfaces as FormTemplateSection).fields.ownnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ownnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
          if (data.dataInterfaces && data.dataInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.dataInterfaces[0].ownnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ownnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.MavenirRCSAS: {
      const formTemplate = (template.contentInterfaces_directFromAF as FormTemplateSection);
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.contentInterfaces_directFromAF as FormTemplateSection).fields.ownnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.ownnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
          if (data.contentInterfaces && data.contentInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.contentInterfaces[0].ownnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.ownnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.HPAAA: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.NokiaSiemensHlrHssPgrw: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.ALUHLR: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);
          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      break;
    }
    case AFTypes.OracleCamiantPcrf: {
      const formTemplate = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'mdf2NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.cdnetid.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          const filteredNetworks = (networks ?? []).filter((n: Network) => !n.nodeName || n.nodeName === fieldValue[0].value);
          formTemplate.fields.cdnetid.options = [];
          setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['cdnetid']);

          if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
            if (filteredNetworks.length > 0) {
              data.provisioningInterfaces[0].cdnetid = filteredNetworks[0].id;
            }
          }
        } else {
          formTemplate.fields.cdnetid.options = originalOptions;
        }
      }
      if (fieldName === 'mdf3NodeNameMultiSelect') {
        const originalOptions = cloneDeep((original.provisioningInterfaces as FormTemplateSection).fields.ccdestip.options) ?? [];
        if (_.isArray(fieldValue) && fieldValue.length > 0) {
          adaptCcdestipField(formTemplate.fields.ccdestip, xcipioNodes ?? [], fieldValue[0].value);
        } else {
          formTemplate.fields.ccdestip.options = originalOptions;
        }
      }
      break;
    }
    default: {
      break;
    }
  }
}

function adaptCcdestipField(ccdestip: FormTemplateField, xcipioNodes: NodeData[], nodeName: string) {
  const node = xcipioNodes?.find((node) => node.nodeName === nodeName);
  if (node) {
    const ipV4 = node.ipAddress;
    const ipV6 = node.ipAddress6;
    if (ccdestip) {
      const newCcdestipOptions = [
        { label: ipV4, value: ipV4 },
        { label: ipV6, value: ipV6 }
      ]
      ccdestip.options = newCcdestipOptions;
      ccdestip.initial = ipV4;
    }
  }
}
